import { KeyIcon } from '@src/components/icons/KeyIcon';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Hint, Stack, Textarea, showDialog } from '@src/components';

type TransferContext = { destination: string; transferredCards: { name: string; box: string }[]; destinationCards: { name: string; box: string }[] };
function AccessLinkMessage({ userId, transfer }: { userId: number; transfer?: TransferContext }) {
  const { i18n } = useTranslation();
  const ru = i18n.language.startsWith('ru');
  const started = useRef(false);
  const pending = useRef(false);
  const [busy, setBusy] = useState(false);
  const [url, setUrl] = useState('');
  const [message, setMessage] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [status, setStatus] = useState('');
  const [failed, setFailed] = useState(false);
  async function generate() {
    if (pending.current) return;
    pending.current = true;
    setBusy(true);
    setStatus(''); setFailed(false);
    try {
      const response = await fetch(`/api/admin/help-desk/users/${userId}/access-link`, {
        method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' }, body: '{}',
      });
      if (!response.ok) throw new Error(response.status >= 500 ? 'Server unavailable' : 'Unavailable');
      const data: { url: string; expiresAt: string } = await response.json();
      const cardList = (cards: { name: string; box: string }[]) => cards.map(card => `• ${card.name || (ru ? 'Карточка без имени' : 'Unnamed card')} — ${ru ? 'игра' : 'game'} «${card.box || (ru ? 'Без названия' : 'Untitled')}»`).join('\n');
      const transferIntro = transfer ? (ru
        ? `Мы привязали ваши карточки к аккаунту ${transfer.destination}\n\nТеперь в вашем аккаунте находятся карточки:\n${cardList(transfer.destinationCards)}\n\nВот ваша одноразовая персональная ссылка для входа:`
        : `We transferred your cards:\n${cardList(transfer.transferredCards)}\n\nTo account: ${transfer.destination}.\n\nThis account now contains these cards:\n${cardList(transfer.destinationCards)}\n\nHere is your one-time personal sign-in link:`) : null;
      const template = ru
        ? `Здравствуйте! Спасибо, что обратились к нам.\n\n${transferIntro ?? 'Мы создали для вас одноразовую персональную ссылку для восстановления доступа:'}\n${data.url}\n\nОткройте ссылку и нажмите «Войти». Ссылка действует 24 часа и может быть использована только один раз.\n\nПосле входа привяжите и подтвердите email в настройках профиля, если ещё не сделали этого. Там же можно установить пароль и создать и сохранить коды восстановления на случай, если снова потеряете доступ.`
        : `Hello! Thank you for contacting us.\n\n${transferIntro ?? 'We have created a one-time personal link to restore your access:'}\n${data.url}\n\nOpen the link and click “Sign in”. The link expires in 24 hours and can only be used once.\n\nAfter signing in, link and verify your email in your profile settings if you haven't already. You can also set a password and generate and save recovery codes there in case you lose access again.`;
      setMessage(current => url ? (current.includes(url) ? current.replaceAll(url, data.url) : `${current}\n\n${data.url}`) : template);
      setUrl(data.url);
      setExpiresAt(data.expiresAt);
    } catch (error) {
      setFailed(true);
      const unavailable = error instanceof TypeError || (error instanceof Error && error.message === 'Server unavailable');
      setStatus(unavailable
        ? (ru ? 'Сервер временно недоступен. Попробуйте создать ссылку ещё раз через несколько секунд. Сообщение для копирования появится после создания ссылки.' : 'The server is temporarily unavailable. Try generating the link again in a few seconds. The message to copy will appear once the link is created.')
        : (ru ? 'Не удалось создать ссылку. Попробуйте ещё раз.' : 'Could not generate the link. Please retry.'));
    } finally { pending.current = false; setBusy(false); }
  }
  useEffect(() => {
    if (!started.current) { started.current = true; void generate(); }
    // Generate once when this dialog opens, not when the editable message changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  async function copy(value: string) {
    try { await navigator.clipboard.writeText(value); setFailed(false); setStatus(ru ? 'Скопировано.' : 'Copied.'); }
    catch { setFailed(true); setStatus(ru ? 'Не удалось скопировать. Выделите текст и скопируйте вручную.' : 'Could not copy. Select and copy the text manually.'); }
  }
  return <Stack gap={3}>
    <p>{ru ? `${transfer ? transfer.destination : `Пользователь #${userId}`}. Новая ссылка отменяет предыдущую. Сообщение не отправляется автоматически.` : `${transfer ? transfer.destination : `User #${userId}`}. A new link invalidates the previous one. The message is not sent automatically.`}</p>
    {url && <>
      <Textarea label={ru ? 'Сообщение пользователю' : 'Message to the user'} rows={10} autosize={false} value={message} onValueChange={setMessage} disabled={busy} />
      <p>{ru ? 'Действует до: ' : 'Expires: '}{new Date(expiresAt).toLocaleString(i18n.language)}</p>
      <Button disabled={busy} onClick={() => void copy(message)}>{ru ? 'Скопировать сообщение' : 'Copy message'}</Button>
      <Button variant="secondary" disabled={busy} onClick={() => void copy(url)}>{ru ? 'Скопировать ссылку' : 'Copy link'}</Button>
    </>}
    <Button variant="secondary" loading={busy} disabled={busy} onClick={() => void generate()}>{url ? (ru ? 'Создать новую ссылку' : 'Regenerate link') : (ru ? 'Создать ссылку' : 'Generate link')}</Button>
    {status && (failed ? <Hint tone="error">{status}</Hint> : <p role="status">{status}</p>)}
  </Stack>;
}

export function openHelpDeskAccessLink(userId: number, ru: boolean, transfer?: TransferContext) {
  showDialog({
    title: ru ? 'Персональная ссылка для входа' : 'Personal sign-in link',
    children: <AccessLinkMessage key={userId} userId={userId} transfer={transfer} />,
    closeLabel: ru ? 'Закрыть' : 'Close',
  });
}

export function HelpDeskAccessLink({ userId }: { userId: number }) {
  const { i18n } = useTranslation();
  const ru = i18n.language.startsWith('ru');
  return <Button variant="ghost" className="help-desk__action-button" icon={<KeyIcon />} aria-label={ru ? 'Создать ссылку для входа' : 'Generate access link'} title={ru ? 'Создать ссылку для входа' : 'Generate access link'} onClick={() => openHelpDeskAccessLink(userId, ru)} />;
}
